/** Change a member's role: one select over the roles the signed-in member may grant. */
import { useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { SubmitButton } from '@/components/SubmitButton';

export interface RoleOption {
  value: string;
  label: string;
}

export interface RoleDialogProps {
  /** The member being changed (shown in the title); `null` closes the dialog. */
  who: string | null;
  initial: string;
  options: RoleOption[];
  onSubmit: (role: string) => Promise<void>;
  onClose: () => void;
  errorMessage: (err: unknown) => string;
}

export function RoleDialog({ who, ...rest }: RoleDialogProps) {
  // Remount per member so the select starts at that member's role.
  return who === null ? null : (
    <RoleDialogBody key={`${who}|${rest.initial}`} who={who} {...rest} />
  );
}

function RoleDialogBody({
  who,
  initial,
  options,
  onSubmit,
  onClose,
  errorMessage,
}: RoleDialogProps & { who: string }) {
  const { t } = useTranslation();
  const [role, setRole] = useState(
    options.some((option) => option.value === initial) ? initial : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; requestId: string | undefined } | null>(
    null,
  );

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(role);
      onClose();
    } catch (err) {
      setError({
        message: errorMessage(err),
        requestId: err instanceof ApiError ? err.requestId : undefined,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(event) => void handleSubmit(event)}>
        <DialogTitle>{t('members.roleDialog.title', { who })}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            select
            fullWidth
            margin="dense"
            label={t('members.field.role')}
            value={role}
            onChange={(event) => setRole(event.target.value)}
            disabled={busy}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={busy} disabled={role === initial || role === ''}>
            {t('members.save')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
